import { Box, Button, Step, StepLabel, Stepper } from '@mui/material'
import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { ShowDialogProps } from 'src/show-dialog'
import { UploadStep, UploadedFile } from './steps/UploadStep'
import { ProcessingStep, ProcessingStatus } from './steps/ProcessingStep'
import { ReviewStep } from './steps/ReviewStep'
import { CertificateExtractionResult } from 'src/api/certificate-extraction'
import {
  invalidateProductCertificationByProductIdCache,
} from 'src/api/product-certification'
import { invalidateComponentsCache } from 'src/api/components'
import { axios } from 'src/axios'
import { useUserCtx } from 'src/hooks/context-hooks'

interface AICertificateUploadWizardProps extends ShowDialogProps {
  productId?: EntityId
}

export const AICertificateUploadWizard: React.FC<AICertificateUploadWizardProps> = ({ pop, productId }) => {
  const { t } = useTranslation(['product-certification', 'common', 'certificate'])
  const { user } = useUserCtx()

  if (user?.encryptedPermission && !axios.defaults.headers['X-Permissions']) {
    axios.defaults.headers['X-Permissions'] = user.encryptedPermission
  }
  const [activeStep, setActiveStep] = useState(0)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [processingStatuses, setProcessingStatuses] = useState<Record<number, ProcessingStatus>>({})
  const [extractionResults, setExtractionResults] = useState<CertificateExtractionResult[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const steps = [
    t('uploadFiles', { ns: 'product-certification' }),
    t('processing', { ns: 'product-certification' }),
    t('reviewAndSave', { ns: 'product-certification' }),
  ]

  const startProcessing = useCallback(async () => {
    setActiveStep(1)

    const statuses: Record<number, ProcessingStatus> = {}
    uploadedFiles.forEach(f => { statuses[f.fileId] = 'processing' })
    setProcessingStatuses(statuses)

    try {
      const fileIds = uploadedFiles.map(f => f.fileId)
      const response = await axios.post('product-certificates/extract-bulk', { fileIds })
      const apiResults = response.data as CertificateExtractionResult[]

      const results = apiResults.map(r => {
        const file = uploadedFiles.find(f => f.fileId === r.fileId)
        return {
          ...r,
          fileName: file?.fileName || r.fileName,
          fileUrl: file?.fileUrl || r.fileUrl,
          viewToken: file?.viewToken || r.viewToken,
        }
      })

      const finalStatuses: Record<number, ProcessingStatus> = {}
      results.forEach(r => {
        const hasFailed = r.notes?.confidence === 'low' && r.notes?.value?.startsWith('Extraction failed')
        finalStatuses[r.fileId] = hasFailed ? 'error' : 'done'
      })
      setProcessingStatuses(finalStatuses)
      setExtractionResults(results)
    } catch (err) {
      const axiosErr = err as any
      const responseData = axiosErr?.response?.data
      const detail = typeof responseData === 'string'
        ? responseData
        : responseData?.message || responseData?.error || responseData?.title
      const errorMsg = detail || axiosErr?.message || 'Unknown error'
      console.error('Bulk extraction failed:', errorMsg, axiosErr?.response)
      toast.error(errorMsg)

      const errorStatuses: Record<number, ProcessingStatus> = {}
      uploadedFiles.forEach(f => { errorStatuses[f.fileId] = 'error' })
      setProcessingStatuses(errorStatuses)

      setExtractionResults(uploadedFiles.map(f => ({
        detected_pattern: 0,
        supplier_name: { value: null, confidence: 'not_found' as const },
        product_number: { value: null, confidence: 'not_found' as const },
        product_name: { value: null, confidence: 'not_found' as const },
        batch_numbers: { value: [], confidence: 'not_found' as const },
        certificate_number: { value: null, confidence: 'not_found' as const },
        certificate_date: { value: null, confidence: 'not_found' as const },
        mbl: { value: null, confidence: 'not_found' as const },
        notes: { value: errorMsg, confidence: 'low' as const },
        is_multi_item: false,
        fileId: f.fileId,
        fileName: f.fileName,
        fileUrl: f.fileUrl,
        viewToken: f.viewToken,
      })))
    }

    setActiveStep(2)
  }, [uploadedFiles])

  const handleSaveAll = async () => {
    setIsSaving(true)
    let successCount = 0
    let skipped = 0

    for (const result of extractionResults) {
      const resolvedProductId = productId ? (productId as number) : result.selectedProductId
      if (!resolvedProductId) {
        skipped++
        toast.warning(`${result.fileName}: ${t('selectProductFirst', { ns: 'product-certification' })}`)
        continue
      }

      const batchNumbers = Array.isArray(result.batch_numbers?.value)
        ? result.batch_numbers.value.map(b => (typeof b === 'string' ? b : b.raw)).join(',')
        : ''

      const notesParts: string[] = []
      if (result.notes?.value) notesParts.push(result.notes.value)
      if (result.certificate_number?.value) notesParts.push(`Cert: ${result.certificate_number.value}`)
      if (result.certificate_date?.value) notesParts.push(`Date: ${result.certificate_date.value}`)
      if (result.mbl?.value) notesParts.push(`MBL: ${result.mbl.value}`)

      try {
        await axios.post('product-certificates/save', {
          productId: resolvedProductId,
          fileId: result.fileId,
          batchNumbers,
          notes: notesParts.join(' | '),
        })
        successCount++
      } catch {
        toast.error(
          t('errorSavingCertificate', {
            ns: 'product-certification',
            defaultValue: `Failed to save: ${result.fileName}`,
          }),
        )
      }
    }

    setIsSaving(false)

    if (successCount > 0) {
      toast.success(
        t('certificatesSaved', {
          ns: 'product-certification',
          defaultValue: `${successCount} certificate(s) saved`,
        }),
      )
      if (productId) {
        invalidateProductCertificationByProductIdCache.getProductCertificationByProductId(productId)
      }
      invalidateComponentsCache.getComponents()
      pop()
    }
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <UploadStep uploadedFiles={uploadedFiles} onFilesChange={setUploadedFiles} />
      case 1:
        return <ProcessingStep files={uploadedFiles} statuses={processingStatuses} />
      case 2:
        return <ReviewStep results={extractionResults} onResultsChange={setExtractionResults} fixedProductId={productId} />
      default:
        return null
    }
  }

  const renderActions = () => {
    switch (activeStep) {
      case 0:
        return [
          <Button key="next" variant="contained" onClick={startProcessing} disabled={uploadedFiles.length === 0}>
            {t('extractData', { ns: 'product-certification' })}
          </Button>,
        ]
      case 1:
        return []
      case 2: {
        const readyCount = productId
          ? extractionResults.length
          : extractionResults.filter(r => r.selectedProductId).length
        return [
          <Button
            key="save"
            variant="contained"
            color="primary"
            onClick={handleSaveAll}
            disabled={isSaving || readyCount === 0}
          >
            {isSaving
              ? t('saving', { ns: 'product-certification' })
              : t('saveAll', { ns: 'product-certification', count: readyCount })}
          </Button>,
        ]
      }
      default:
        return []
    }
  }

  return (
    <GenericDialogLayout
      title={t('smartUpload', { ns: 'product-certification' })}
      pop={() => pop()}
      actions={renderActions()}
    >
      <Box sx={{ width: '100%', py: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 400 }}>{renderStepContent()}</Box>
      </Box>
    </GenericDialogLayout>
  )
}
